import { IPatient } from '@alberta/konexi-shared';
import { Injectable } from '@angular/core';
import { AttachmentDB } from '@common/repository/databases';
import { SQLiteQueryBuilder } from '@common/storage/sqlite-query-builder';
import { AttachmentUtilsService } from '@services/attachment/attachment-utils.service';
import { QueryService } from '@services/query/query.service';
import { AttachmentDto } from 'src/app/shared/models/attachment/attachment-dto.model';

@Injectable()
export class OrderAttachmentListService {
  constructor(
    private _queryService: QueryService,
    private _attachmentUtilsService: AttachmentUtilsService
  ) {}

  async getAttachmentTypes(
    patientId: string,
    orderId: string,
    orderIdFieldName: 'singleOrderId' | 'serviceOrderId'
  ): Promise<number[]> {
    return (
      await this._queryService.raw(
        {
          sql: new SQLiteQueryBuilder()
            .select('type as _id')
            .from('attachment_fts')
            .where('patientId', '=', patientId)
            .and(orderIdFieldName, '=', orderId)
            .and('archived', '<>', 'true')
            .groupBy('type')
            .asString(),
          aggregation: [
            {
              $match: {
                'metadata.patientId': patientId,
                [`metadata.${orderIdFieldName}`]: orderId,
                'metadata.archived': { $ne: true },
              },
            },
            { $group: { _id: '$metadata.type' } },
          ],
        },
        AttachmentDB
      )
    ).map(item => item._id);
  }

  async getTotal(
    patientId: string,
    orderId: string,
    orderIdFieldName: 'singleOrderId' | 'serviceOrderId',
    attachmentType: number
  ): Promise<number> {
    // SQL inner join is needed because local created attachements are added with a local| prefix
    // to the fts table. If the attachment is uploaded to the server the attachment in the database
    // is replaced with the server version. see attachment-database.service.ts "replaceLocalData"
    // unfortunately the fts table is not updated. So we need to join the fts table with the attachment table.
    const total = (
      await this._queryService.raw(
        {
          sql: new SQLiteQueryBuilder()
            .select('COUNT(*) AS count')
            .from('attachment_fts')
            .where('patientId', '=', patientId)
            .and(orderIdFieldName, '=', orderId)
            .and('type', '=', `${attachmentType}`)
            .and('archived', '<>', 'true')
            .innerJoin('attachment', 'id', '=', 'key')
            .asString(),
          aggregation: [
            {
              $match: {
                'metadata.patientId': patientId,
                [`metadata.${orderIdFieldName}`]: orderId,
                'metadata.type': parseInt(attachmentType.toString(), 10),
                'metadata.archived': { $ne: true },
              },
            },
            {
              $group: { _id: '$metadata.type', count: { $sum: 1 } },
            },
          ],
        },
        AttachmentDB
      )
    ).map(item => item.count)[0];

    return total ?? 0;
  }

  async getAttachments(
    patient: IPatient,
    orderId: string,
    orderIdFieldName: 'singleOrderId' | 'serviceOrderId',
    attachmentType: number
  ): Promise<AttachmentDto[]> {
    // SQL inner join is needed because local created attachements are added with a local| prefix
    // to the fts table. If the attachment is uploaded to the server the attachment in the database
    // is replaced with the server version. see attachment-database.service.ts "replaceLocalData"
    // unfortunately the fts table is not updated. So we need to join the fts table with the attachment table.
    const attachments = await this._queryService.raw(
      {
        sql: new SQLiteQueryBuilder()
          .selectAll()
          .from('attachment_fts')
          .where('patientId', '=', patient._id)
          .and(orderIdFieldName, '=', orderId)
          .and('type', '=', `${attachmentType}`)
          .and('archived', '<>', 'true')
          .orderBy('createdAt', 'DESC')
          .innerJoin('attachment', 'id', '=', 'key')
          .asString(),
        aggregation: [
          {
            $match: {
              'metadata.patientId': patient._id,
              [`metadata.${orderIdFieldName}`]: orderId,
              'metadata.type': parseInt(attachmentType.toString(), 10),
              'metadata.archived': { $ne: true },
            },
          },
          {
            $sort: { 'metadata.createdAt': -1 },
          },
          {
            $skip: 0,
          },
        ],
      },
      AttachmentDB
    );

    return Promise.all(
      attachments.map(async attachment => {
        const displayName = await this._attachmentUtilsService.getDisplayFileName(patient, attachment);
        return { ...attachment, displayName };
      })
    );
  }
}
